import type { FileCollectionTable } from "../types/files";

/**
 * The collections a person may file an inbox file into by hand, and the whole of them.
 *
 * All three hang off a project, which is what makes one picker enough for the set: choose the
 * project, then the record within it. The other twelve registry collections — a vehicle document,
 * an article image, a return sheet — are reached from the record they belong to and have no
 * business being offered as a destination for something somebody dragged onto the window. An
 * outgoing invoice is one of them: it is written here, and its scan is attached where it is
 * booked, so an invoice somebody drops into the inbox was written by somebody else.
 */
export const FILE_FILING_COLLECTIONS = [
	"projectDocumentFiles",
	"ticketFiles",
	"protocolFiles",
] as const satisfies readonly FileCollectionTable[];

export type FileFilingCollection = (typeof FILE_FILING_COLLECTIONS)[number];

/**
 * Which foreign key column each collection wants the chosen record's id in, or null where the
 * project alone is the destination.
 *
 * `ticketFiles` is the one that writes two columns: a ticket file row belongs to the ticket and to
 * the project the ticket sits on, and the project column is what a project's own gallery reads.
 * The server proves the two agree — `assertParentsAgree` refuses a ticket that is not on the named
 * project — so this map is a convenience, never the check.
 */
const RECORD_COLUMNS = {
	projectDocumentFiles: null,
	ticketFiles: "ticketId",
	protocolFiles: "protocolId",
} as const satisfies Record<FileFilingCollection, string | null>;

/**
 * Whether filing into this collection needs a record within the project as well as the project.
 *
 * @param collection - The collection being filed into.
 * @returns Whether a record has to be chosen.
 */
export function fileFilingNeedsRecord(collection: FileFilingCollection): boolean {
	return RECORD_COLUMNS[collection] !== null;
}

/**
 * The foreign key values a filing request carries, from what the person picked.
 *
 * @param collection - The collection being filed into.
 * @param projectId - The project chosen.
 * @param recordId - The record within it, where the collection needs one.
 * @returns The column/value pairs the filing route writes, or null when a needed record is missing.
 */
export function fileFilingTarget(
	collection: FileFilingCollection,
	projectId: string,
	recordId: string | null,
): Record<string, string> | null {
	const column = RECORD_COLUMNS[collection];
	if (!column) return { projectId };
	if (!recordId) return null;
	// The project travels with the ticket because both columns are `NOT NULL`; a protocol carries
	// its own project and would refuse a second parent the registry cannot address.
	if (collection === "ticketFiles") return { ticketId: recordId, projectId };
	return { [column]: recordId };
}
